import {Component, OnInit} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {DeliveryService} from 'src/app/services/delivery.service';
import {Delivery, PaginatedDeliveryResponse} from 'src/app/models/delivery.model';
import {environment} from 'src/environments/environment';
import {ToastrService} from "ngx-toastr";
import {ExcelExportService} from "../../services/excel-export.service";
import {AuditLogsService, OneKashaAuditPayload} from "../../services/auditlogs.service";
import {Checktoken} from "../../models/checktoken.model";
import {saveAs} from "file-saver";
import {CargService} from "../../services/carg.service";
import {CargGroup} from "../../models/carg.model";
import {MatDialog} from "@angular/material/dialog";
import {EditDeliveryModalComponent} from "../edit-delivery-modal/edit-delivery-modal.component";

declare var bootstrap: any;

@Component({
    selector: 'app-delivery-dashboard',
    templateUrl: './delivery-dashboard.component.html',
    styleUrls: ['./delivery-dashboard.component.css']
})
export class DeliveryDashboardComponent implements OnInit {
    isLoading = false;
    deliveries: Delivery[] = [];
    filteredDeliveries: Delivery[] = [];
    totalItems = 0;
    currentPage = 1;
    pageSize = environment.deliveryFormPageSize;
    totalDeliveries = 0;
    successfulDeliveries = 0;
    failedDeliveries = 0;
    pendingDeliveries = 0;
    missedDeliveries = 0;
    dueDeliveries = 0;
    selectedDelivery: any = null;
    deliveryForm!: FormGroup;
    cargGroups: CargGroup[] = [];
    filterForm!: FormGroup;
    expandedRow: Delivery | null = null;   // 👈 Track which row is expanded
    orgId: number = 0;
    loading: boolean = false;

    /** True when the active tenant is RW (either NCD Sanofi or PDS/FMCG). */
    get isRwTenant(): boolean {
        return this.orgId === environment.rw_ncdSanofiID
            || this.orgId === environment.rw_pdsID;
    }




    constructor(
        private deliveryService: DeliveryService,
        private fb: FormBuilder,
        private toaster: ToastrService,
        private excelService: ExcelExportService,
        private auditLogsService: AuditLogsService,
        private cargService: CargService,
        private dialog: MatDialog
    ) {
    }

    ngOnInit(): void {

        this.orgId = Number(localStorage.getItem('orgId'));

        this.deliveryForm = this.fb.group({
            client_id: ['', Validators.required],
            delivery_type: ['', Validators.required],
            rider_name: ['', Validators.required],
            county: [''],
            facility: ['', Validators.required],
            delivery_outcome: ['', Validators.required],
            actual_delivery_date: [''],
            delivery_time: [''],
            delivery_location_name: [''],
            comments: [''],
            medication_returned: [false]
        });

        this.filterForm = this.fb.group({
            facility_name: [''],
            ccc: [''],
            carg: [''],
            refill_start_date: [''],
            refill_end_date: [''],
            prefered_delivery_start_date : [''],
            prefered_delivery_end_date: [''],
            actual_delivery_start_date: [''],
            actual_delivery_end_date: [''],
            delivery_outcome: [''],
            dynamics_order_number: [''],
            page: [1],
            page_size: [environment.deliveryFormPageSize]
        });
        this.getCargs();
        this.loadDeliveries();
        this.auditLogsService.postLog("View Deliveries").subscribe({
            next: (response: Checktoken) => {

            }, error: (error) => {

            }
        });
    }

    private getActiveFilters(): Record<string, any> {
        const raw = this.filterForm?.value || {};
        const active: Record<string, any> = {};

        const formatDate = (val: any): string => {
            if (!val) return '';
            if (val instanceof Date) {
                const year = val.getFullYear();
                const month = String(val.getMonth() + 1).padStart(2, '0');
                const day = String(val.getDate()).padStart(2, '0');
                return `${year}-${month}-${day}`;
            }
            return String(val);
        };

        Object.entries(raw).forEach(([key, val]) => {
            if (key === 'page' || key === 'page_size') {
                return;
            }
            if (val === null || val === undefined || val === '') {
                return;
            }
            if (typeof val === 'string' && val.trim() === '') {
                return;
            }

            if (val instanceof Date) {
                active[key] = formatDate(val);
            } else if (typeof val === 'object' && val !== null) {
                if ('group_name' in val && (val as any).group_name) {
                    active[key] = (val as any).group_name;
                } else if ('group_identifier' in val && (val as any).group_identifier) {
                    active[key] = (val as any).group_identifier;
                } else if (Object.keys(val).length > 0) {
                    active[key] = val;
                }
            } else if (typeof val === 'string') {
                active[key] = val.trim();
            } else {
                active[key] = val;
            }
        });

        return active;
    }

    private buildDeliveryLogContext(delivery: Delivery | any): Partial<OneKashaAuditPayload> {
        const facility = delivery?.facility_name || "";

        return {
            dynamics_order_no: delivery?.dynamics_order_no
                ? String(delivery.dynamics_order_no)
                : null,
            facility,
            rider_name: delivery?.name_of_rider ? String(delivery.name_of_rider) : null,
            metadata: {
                client_id: delivery?.ccc || null,
                facility_name: facility || null,
                delivery_outcome: delivery?.delivery_outcome || null
            }
        };
    }

    private logFilterResult(
        activeFilters: Record<string, any>,
        result?: {
            results_count?: number;
            has_results?: boolean;
            page?: number;
            page_size?: number;
            stats?: Record<string, any>;
            error?: string;
        }
    ): void {
        this.auditLogsService.postLog("Filter Deliveries", {
            facility: activeFilters['facility_name'] || "",
            dynamics_order_no: activeFilters['dynamics_order_number']
                ? String(activeFilters['dynamics_order_number'])
                : null,
            metadata: {
                applied_filters: activeFilters,
                ...(result || {})
            }
        }).subscribe({ error: () => {} });
    }

    applyFilters(): void {
        this.loadDeliveries(this.currentPage, true);
    }


    resetFilters(): void {
        const previousFilters = this.getActiveFilters();
        this.auditLogsService.postLog("Reset Deliveries Filter", {
            metadata: {
                action: 'reset',
                previous_filters: Object.keys(previousFilters).length > 0 ? previousFilters : undefined
            }
        }).subscribe({ error: () => {} });
        this.filterForm.reset();
        this.loadDeliveries();
    }

    downloadReport(): void {

        this.loading = true;

        const activeFilters = this.getActiveFilters();
        this.auditLogsService.postLog("Download Deliveries Report", {
            facility: activeFilters['facility_name'] || "",
            dynamics_order_no: activeFilters['dynamics_order_number']
                ? String(activeFilters['dynamics_order_number'])
                : null,
            metadata: {
                applied_filters: activeFilters,
                action: 'download_report'
            }
        }).subscribe({
            next: (response) => {},
            error: (error) => {}
        });

        const filters = {
            ...this.filterForm.value
        }

        let apiUrl = `${environment.apiUrl}/scheduled/deliveries/report`

        this.excelService.exportToExcel(filters, apiUrl).subscribe({
            next: (blob) => {
                saveAs(blob, "Deliveries Report");
            },
            error: (error) => {
                this.toaster.error("Unable to generate report");
            }, complete: () => this.loading = false
        });
    }


    openEditModal(delivery: any) {
        const status = delivery.delivery_outcome?.toLowerCase();

        if (!this.isEditableStatus(status)) {
            this.toaster.error("Cannot edit a delivery with "+status+" status");
            return;
        }
        this.selectedDelivery = undefined;

        this.auditLogsService.postLog(
            "Open Edit Delivery Modal",
            this.buildDeliveryLogContext(delivery)
        ).subscribe({ error: () => {} });

        const dialogRef = this.dialog.open(EditDeliveryModalComponent, {
            width: '700px',
            data: {delivery}
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.handleSave(result);
            }
        })


    }


    handleSave(updatedDelivery: any) {

        console.log(updatedDelivery);

        this.deliveryService.updateDelivery(this.formateUpdateDeliveryPaylod(updatedDelivery)).subscribe({
            next: (response) => {
                this.toaster.success("Delivery updated successfully.");
                this.deliveryForm.reset();
                this.loadDeliveries();

                const ctx = this.buildDeliveryLogContext(updatedDelivery);
                this.auditLogsService.postLog("Delivery Status Updated", {
                    ...ctx,
                    metadata: {
                        ...(ctx.metadata || {}),
                        actual_delivery_date: updatedDelivery.actual_delivery_date,
                        actual_delivery_location: updatedDelivery.actual_delivery_location,
                        medication_returned: updatedDelivery.medication_returned,
                        contact_of_rider: updatedDelivery.contact_of_rider
                    }
                }).subscribe({
                    next: (res) => {
                        console.log(res);
                    }, error: (error) => {
                        console.log(error);
                    }
                });
            }, error: (err) => {
                this.toaster.error("Update failed, please try again");
                console.error('🔥 FULL RAW ERROR:', err);
                console.error('🔥 err.error:', err.error);
            }
        });


    }

    formateUpdateDeliveryPaylod(updatedDelivery: any) {
        if (updatedDelivery.ccc) {
            return [
                {
                    id: updatedDelivery.id,
                    delivery_outcome: updatedDelivery.delivery_outcome,
                    actual_delivery_date: updatedDelivery.actual_delivery_date,
                    delivery_time: updatedDelivery.delivery_time,
                    actual_delivery_location: updatedDelivery.actual_delivery_location,
                    medication_returned: updatedDelivery.medication_returned,
                    name_of_rider: updatedDelivery.name_of_rider,
                    contact_of_rider: updatedDelivery.contact_of_rider,
                    desc : updatedDelivery.actual_delivery_location,
                    created_by: localStorage.getItem("email")
                }

            ]
        }else{
            return updatedDelivery.clients.map((client: any) => ({
                ccc: client.ccc,
                delivery_outcome: client.delivery_outcome,
                actual_delivery_date: updatedDelivery.actual_delivery_date,
                delivery_time: updatedDelivery.delivery_time,
                actual_delivery_location: updatedDelivery.actual_delivery_location,
                medication_returned: client.medication_returned,
                name_of_rider: updatedDelivery.name_of_rider,
                contact_of_rider: updatedDelivery.contact_of_rider,
                comments: client.comments,
                prefered_delivery_date: updatedDelivery.prefered_delivery_date,
                group_name: updatedDelivery.group_name,
                delivery_geocoordinates: updatedDelivery.actual_delivery_location,
                created_by: localStorage.getItem("email")
            }));
        }
    }


    isEditableStatus(status: string): boolean {
        // if (!status) return false;
        // const normalized = status.toLowerCase();
        // return normalized === 'due' || normalized === 'missed' || normalized === 'pending';
        return false;
    }


    loadDeliveries(page: number = this.currentPage, logFilter: boolean = false): void {

        this.loading = true;

        const filters = {
            ...this.filterForm.value,
            page,
            page_size: this.pageSize
        }

        this.deliveryService.getDeliveries(filters).subscribe({

            next: (response: PaginatedDeliveryResponse) => {

                this.deliveries = response.items;
                this.filteredDeliveries = response.items;
                this.totalItems = response.total;
                this.currentPage = response.page;
                this.pageSize = response.page_size;
                this.totalDeliveries = response.stats.total_deliveries;
                this.successfulDeliveries = response.stats.successful_deliveries;
                this.failedDeliveries = response.stats.unsuccessful_deliveries;
                this.pendingDeliveries = response.stats.pending_deliveries + response.stats.postponed_deliveries;
                this.dueDeliveries = response.stats.due_deliveries;
                this.missedDeliveries = response.stats.missed_deliveries;
                this.isLoading = false;

                if (logFilter) {
                    this.logFilterResult(this.getActiveFilters(), {
                        results_count: response.total,
                        has_results: !!(response.items && response.items.length > 0),
                        page: response.page,
                        page_size: response.page_size,
                        stats: {
                            total_deliveries: response.stats?.total_deliveries,
                            successful_deliveries: response.stats?.successful_deliveries,
                            unsuccessful_deliveries: response.stats?.unsuccessful_deliveries,
                            due_deliveries: response.stats?.due_deliveries,
                            missed_deliveries: response.stats?.missed_deliveries,
                            pending_deliveries: (response.stats?.pending_deliveries || 0) + (response.stats?.postponed_deliveries || 0)
                        }
                    });
                }

            }, error: (err) => {
                console.error('Failed to load deliveries');
                this.isLoading = false;

                if (logFilter) {
                    this.logFilterResult(this.getActiveFilters(), {
                        error: err?.message || 'Failed to load deliveries',
                        has_results: false,
                        results_count: 0
                    });
                }
            }, complete: () => this.loading = false
        });
    }

    get totalPages(): number {
        return Math.ceil(this.totalItems / this.pageSize);
    }

    nextPage() {
        if (this.currentPage < this.totalPages) {
            this.currentPage++;
            this.auditLogsService.postLog("Paginate Deliveries", {
                metadata: {
                    direction: 'next',
                    page: this.currentPage,
                    total_pages: this.totalPages
                }
            }).subscribe({ error: () => {} });
            this.loadDeliveries();
        }
    }

    prevPage() {
        if (this.currentPage > 1) {
            this.currentPage--;
            this.auditLogsService.postLog("Paginate Deliveries", {
                metadata: {
                    direction: 'prev',
                    page: this.currentPage,
                    total_pages: this.totalPages
                }
            }).subscribe({ error: () => {} });
            this.loadDeliveries();
        }
    }


    getDisplayOutcome(outcome?: string): string {
        const value = (outcome || '').toLowerCase();
        if (value === 'successful' || value === 'not successful' || value === 'due' || value === 'missed' || value === 'delivered') {
            return outcome || '';
        }
        return 'Pending';
    }

    getCargs() {
        this.cargService.getCargs().subscribe({
            next: (response) => {
                this.cargGroups = response;
            }, error: (error) => {
                console.log("Unable to fetch carg groups", error);
            }
        });
    }

    /** Given an ID, return the matching name (or empty if not found) */
    displayCargName(carg: CargGroup): string {
        return carg?.group_name ?? '';
    }

    toggleRow(delivery: Delivery): void {
        if (this.expandedRow === delivery) {
            this.expandedRow = null;   // collapse if already expanded
        } else {
            this.expandedRow = delivery;  // expand this one
            const ctx = this.buildDeliveryLogContext(delivery);
            this.auditLogsService.postLog("View Delivery History", {
                ...ctx,
                metadata: {
                    ...(ctx.metadata || {}),
                    attempt_count: delivery.attempt_count
                }
            }).subscribe({ error: () => {} });
        }
    }

    // If you want only rows with multiple attempts expandable:
    canExpand(delivery: Delivery): boolean {
        return delivery.attempt_count > 1;
    }

    protected readonly environment = environment;
}
